import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate, label } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { Card, Empty, Field, PageHeader, Td, Th } from "../../components/common/UI";
import { DueBadge } from "../../components/measure/PostApprovalTab";
import { Button } from "../../components/ui/button";

const LEVEL = { overdue: "bg-red-50 border-red-200 text-red-800", due_soon: "bg-amber-50 border-amber-200 text-amber-900", upcoming: "bg-white border-slate-200 text-slate-700" };
const mlink = (r) => <Link className="font-medium text-sky-700 hover:underline" to={`/measures/${r.measure_id}?tab=post-approval`}>{r.measure_ref}</Link>;
const todayIso = () => new Date(Date.now() + 8 * 3600000).toISOString().slice(0, 10);

function useList(path) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(path).then((r) => setRows(r.data)); }, [path]);
  return rows;
}

export function DeadlineAlerts({ kinds }) {
  const rows = useList("/deadlines");
  const list = (rows || []).filter((a) => !kinds || kinds.includes(a.kind));
  return (
    <Card title="Deadline alerts" testId="deadline-alerts" className="mb-5">
      {rows && list.length === 0 && <Empty text="No pending deadlines." />}
      <div className="space-y-2">
        {list.map((a, i) => (
          <div key={i} className={`flex flex-wrap items-center gap-2 rounded border px-3 py-2 text-xs ${LEVEL[a.level]}`} data-testid={`alert-${a.kind}-${a.measure_id}`}>
            {mlink({ ...a, measure_ref: a.ref })}<span className="flex-1">{a.message} — {fmtDate(a.due_date)}</span>
            <span className="font-semibold uppercase">{a.level === "overdue" ? `${-a.days_left} day(s) overdue` : a.days_left === 0 ? "today" : `${a.days_left} day(s)`}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Table({ rows, head, cells, testId }) {
  return (
    <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
      <table className="w-full min-w-[760px]" data-testid={testId}>
        <thead><tr>{head.map((h) => <Th key={h}>{h}</Th>)}</tr></thead>
        <tbody>{(rows || []).map((r) => <tr key={r.id || r.measure_id}>{cells(r).map((c, i) => <Td key={i}>{c}</Td>)}</tr>)}</tbody>
      </table>
      {rows && rows.length === 0 && <Empty />}
    </div>
  );
}

export function ExecutiveActions() {
  const rows = useList("/executive-actions");
  const t = todayIso();
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Post-approval" title="Executive Actions" subtitle="Measures transmitted to the Local Chief Executive: approval, veto, deemed approval and veto overrides. Record actions from the measure's Post-approval tab." />
      <DeadlineAlerts kinds={["executive"]} />
      <Table rows={rows} testId="executive-table" head={["Measure", "Received", "Deadline", "Action", "Override"]} cells={(r) => [
        <>{mlink(r)}<div className="text-[11px] text-slate-500">{r.measure_title}</div></>, fmtDate(r.received_date),
        <span className="flex flex-wrap items-center gap-1">{fmtDate(r.deadline_date)}{r.deadline_manual && <i className="text-[10px]">(manual)</i>}<DueBadge due={r.deadline_date} today={t} done={!!r.action} /></span>,
        r.action ? `${label(r.action)} · ${fmtDate(r.action_date)}` : <span className="text-amber-700">Pending</span>, r.override_status ? label(r.override_status) : "—"]} />
    </div>
  );
}

export function ExternalReviews() {
  const rows = useList("/reviews");
  const t = todayIso();
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Post-approval" title="External Review" subtitle="Submissions to the reviewing authority, review periods and findings." />
      <DeadlineAlerts kinds={["review"]} />
      <Table rows={rows} testId="reviews-table" head={["Measure", "Reviewing authority", "Submitted", "Deadline", "Result"]} cells={(r) => [
        <>{mlink(r)}<div className="text-[11px] text-slate-500">{r.measure_title}</div></>, r.reviewing_body, fmtDate(r.submitted_date),
        <span className="flex flex-wrap items-center gap-1">{fmtDate(r.deadline_date)}<DueBadge due={r.deadline_date} today={t} done={r.status !== "pending"} /></span>, label(r.status)]} />
    </div>
  );
}

export function PublicationMonitor() {
  const rows = useList("/publication-monitor");
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Post-approval" title="Publication and Effectivity" subtitle="Posting in conspicuous places, newspaper publication where required, and effectivity dates." />
      <DeadlineAlerts kinds={["publication", "effectivity"]} />
      <Table rows={rows} testId="publication-monitor-table" head={["Measure", "Stage", "Posting", "Newspaper", "Effectivity"]} cells={(r) => [
        <>{mlink(r)}<div className="text-[11px] text-slate-500">{r.measure_title}</div></>, label(r.measure_status),
        <span className={r.posting.places_with_proof >= r.posting.min_places ? "text-emerald-700" : "text-amber-700"}>{r.posting.places_with_proof}/{r.posting.min_places} with proof</span>,
        r.posting.newspaper_required ? (r.posting.newspapers_with_proof ? "Published" : "Required") : "Not required",
        r.effectivity?.effectivity_date ? <>{fmtDate(r.effectivity.effectivity_date)}{r.effectivity.verified_at && " · verified"}</> : "—"]} />
    </div>
  );
}

const NUMS = [["lce_days", "Executive action period (days)"], ["review_days", "External review period (days)"], ["effectivity_days", "Effectivity after posting (days)"],
  ["min_posting_places", "Minimum conspicuous posting places"], ["alert_days_before", "Alert this many days before a deadline"]];
const BOOLS = [["order_attestation", "Order of business: Presiding Officer attestation"], ["order_adoption_field", "Order of business: adoption / approval field"],
  ["rollcall_confirmation", "Roll-call sheet: Presiding Officer confirmation"]];

export function PostApprovalSettings() {
  const { can } = useAuth();
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/post-approval/settings").then((r) => setF(r.data)); }, []);
  if (!f) return <div className="text-sm text-slate-500">Loading…</div>;
  const submit = () => api.put("/post-approval/settings", f).then((r) => { setF(r.data); toast.success("Rules saved"); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Administration" title="Post-approval Rules" subtitle="Defaults from RA 7160 (Secs. 54, 56, 57 and 59). Verify with your Legal Officer. Staff can still set a different deadline on any measure." />
      <Card testId="post-approval-settings">
        <fieldset disabled={!can("lgu.configure")} className="grid gap-4 md:grid-cols-2">
          {NUMS.map(([k, l]) => <Field key={k} label={l}><input type="number" min={0} className="otms-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: Number(e.target.value) })} data-testid={`setting-${k}`} /></Field>)}
          <Field label="Default reviewing authority"><input className="otms-input" value={f.review_body} onChange={(e) => setF({ ...f, review_body: e.target.value })} data-testid="setting-review_body" /></Field>
          <Field label="Veto override vote rule">
            <select className="otms-input" value={f.veto_override_rule} onChange={(e) => setF({ ...f, veto_override_rule: e.target.value })} data-testid="setting-veto_override_rule">
              <option value="two_thirds_all">Two-thirds of all members</option><option value="two_thirds_present">Two-thirds of members present</option><option value="majority_all">Majority of all members</option>
            </select>
          </Field>
          <div className="space-y-2 md:col-span-2">
            <div className="text-xs font-semibold text-slate-700">Printout signature blocks (defaults; can be toggled when printing)</div>
            {BOOLS.map(([k, l]) => <label key={k} className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`setting-${k}`} />{l}</label>)}
          </div>
        </fieldset>
        {can("lgu.configure") && <Button className="mt-5 bg-sky-600 hover:bg-sky-700" size="sm" onClick={submit} data-testid="settings-save">Save rules</Button>}
      </Card>
    </div>
  );
}
